import { Fragment, useState } from "react";

import {
  RenderSeeMoreTextAnswers,
  Table,
  TableData,
  TableHead,
  TableHeader,
  Typography,
} from "~/common/components";
import { YES_NO_PREFER_NOT_TO_SAY_TYPES } from "~/common/utils";
import AudioPlayer from "~/patients/components/ui/AudioPlayer";
import {
  AI_CONVERSATION_HISTORY_CODES,
  ALL_AI_CONVERSATION_HISTORY_CODES,
  PREVIEW_TITLE_CODES,
} from "~/providers/constants/SummariesExpandableIds";
import {
  AIConversationHistory,
  FillmData,
} from "~/providers/utils/types/patientIntakePreviewTypes";
import Expandable from "../common/Expandable";
import { FillmModal } from "./FillmModal";
import { FillmTitle } from "./FillmTitle";

interface FillmProps {
  aiConversationHistory: AIConversationHistory;
  fillm?: FillmData;
}

export const Fillm = ({ aiConversationHistory, fillm }: FillmProps) => {
  const {
    account,
    prompt,
    response,
    evaluation,
    extra_thoughts,
    modifications,
  } = aiConversationHistory;

  const [showInfoModal, setShowInfoModal] = useState<boolean>(false);
  const [seeMoreActive, setSeeMoreActive] = useState<boolean>(false);
  const [seeMoreModificationsActive, setSeeMoreModificationsActive] =
    useState<boolean>(false);

  const onClickSeeMoreButton = () => {
    setSeeMoreActive((prev) => !prev);
  };

  const onClickSeeMoreModificationsButton = () => {
    setSeeMoreModificationsActive((prev) => !prev);
  };

  const extraThoughts = extra_thoughts?.[0];
  const noAIAccountOrPrefersNotToSay =
    account === YES_NO_PREFER_NOT_TO_SAY_TYPES.no ||
    account === YES_NO_PREFER_NOT_TO_SAY_TYPES.prefer_not_to_say;
  const hasAccountButSkipped =
    account === YES_NO_PREFER_NOT_TO_SAY_TYPES.yes && response === "";

  const showActions =
    account === YES_NO_PREFER_NOT_TO_SAY_TYPES.yes && response !== "";

  const renderContent = () => {
    if (noAIAccountOrPrefersNotToSay) {
      return "Patient does not have an account or prefers not to say";
    }

    if (hasAccountButSkipped) {
      return "Patient has an account but opted to skip this section";
    }

    return (
      <Fragment>
        {fillm && (
          <>
            <Typography className="border-b border-neutral-80 pb-3">
              {fillm.intro_sentence}
            </Typography>

            <Expandable.Section
              code={AI_CONVERSATION_HISTORY_CODES.NOVEL_SIGNALS}
            >
              <ul className="list-disc pl-3.5">
                {fillm.novel.bullets.map((bullet, i) => (
                  <li className="pb-1 last:pb-0" key={`novel-${i}`}>
                    {bullet}
                  </li>
                ))}
              </ul>
            </Expandable.Section>
            <Expandable.Section
              code={AI_CONVERSATION_HISTORY_CODES.CONFLICTING_SIGNALS}
            >
              <ul className="list-disc pl-3.5">
                {fillm.conflicting.bullets.map((bullet, i) => (
                  <li className="pb-1 last:pb-0" key={`conflicting-${i}`}>
                    {bullet}
                  </li>
                ))}
              </ul>
            </Expandable.Section>
            <Expandable.Section
              code={AI_CONVERSATION_HISTORY_CODES.CONGRUENT_SIGNALS}
            >
              <ul className="list-disc pl-3.5">
                {fillm.congruent.bullets.map((bullet, i) => (
                  <li className="pb-1 last:pb-0" key={`congruent-${i}`}>
                    {bullet}
                  </li>
                ))}
              </ul>
            </Expandable.Section>
          </>
        )}
        <Expandable.Section
          code={
            AI_CONVERSATION_HISTORY_CODES.AI_SUMMARY_OF_CONVERSATION_HISTORY
          }
        >
          {prompt && response && (
            <p className="mb-2 text-xs italic text-gray-70">
              The patient was asked to run the following prompt using their
              personal AI platform account and share the response.{" "}
              <br className="hidden sm:block" />
              The response has not been processed or validated by PsychNow.
            </p>
          )}
          <div className="flex flex-col gap-2 rounded-md bg-neutral-300 p-3">
            <div className="flex flex-col gap-1">
              <p className="font-semibold text-gray-70">Prompt</p>
              <p className="text-gray-70">{prompt}</p>
            </div>
            <div className="flex flex-col gap-1">
              <p className="font-semibold text-gray-70">Response</p>
              <p className="text-gray-70">{response}</p>
            </div>
          </div>
        </Expandable.Section>
        <Expandable.Section
          code={AI_CONVERSATION_HISTORY_CODES.PATIENT_REFLECTION}
          className="border-b-0 pb-0"
        >
          <Table className="overflow-hidden rounded-md border">
            <TableHead>
              <tr>
                <TableHeader>Question</TableHeader>
                <TableHeader>Response</TableHeader>
              </tr>
            </TableHead>
            <tbody className="text-sm text-gray-70">
              <tr className="py-4">
                <TableData className="py-4 first-letter:uppercase">
                  On a scale of 0 to 5, how well does this summary describe you?
                </TableData>
                <TableData className="py-4 first-letter:uppercase">
                  <div className="flex items-center gap-2">
                    <span>{evaluation}</span>
                    <span className="italic">
                      (0 = not at all, 5 = completely)
                    </span>
                  </div>
                </TableData>
              </tr>
              <tr className="py-4">
                <TableData className="py-4 align-top first-letter:uppercase">
                  How would you modify it?
                </TableData>
                <TableData className="py-4 first-letter:uppercase">
                  <RenderSeeMoreTextAnswers
                    seeMoreActive={seeMoreModificationsActive}
                    onClick={onClickSeeMoreModificationsButton}
                    text={modifications}
                  />
                </TableData>
              </tr>
              <tr className="py-4">
                <TableData className="flex h-full py-4 first-letter:uppercase">
                  With your summary as a backdrop, talk about anything that
                  comes to mind, such as people, scenes, memories, sensations,
                  even if it seems random.
                </TableData>
                <TableData className="py-4 first-letter:uppercase">
                  {extraThoughts && (
                    <>
                      <AudioPlayer
                        thumbNailClassName="blue-thumb bg-primary-200"
                        totalDuration={extraThoughts.duration ?? 0}
                        src={extraThoughts.path}
                        className="w-full border border-primary-200 bg-primary-100 p-2 text-primary-600"
                        iconsClassName="text-primary-600"
                      />
                      {extraThoughts.transcribed_text && (
                        <RenderSeeMoreTextAnswers
                          seeMoreActive={seeMoreActive}
                          onClick={onClickSeeMoreButton}
                          text={extraThoughts.transcribed_text}
                          shortTextClassName="break-words text-sm font-light text-gray-10"
                          longTextClassName="text-sm font-light text-gray-10"
                        />
                      )}
                    </>
                  )}
                </TableData>
              </tr>
            </tbody>
          </Table>
        </Expandable.Section>
      </Fragment>
    );
  };

  return (
    <Expandable.Wrapper
      title={<FillmTitle onClick={() => setShowInfoModal(true)} />}
      code={PREVIEW_TITLE_CODES.AI_CONVERSATION_HISTORY}
      expandableSections={[...ALL_AI_CONVERSATION_HISTORY_CODES]}
      showActions={showActions}
    >
      {renderContent()}

      <FillmModal
        onClose={() => setShowInfoModal(false)}
        show={showInfoModal}
      />
    </Expandable.Wrapper>
  );
};
